import { CHANNEL, EVENTSTATUS, FULLSEATGAP, LAYOUT, RENDERER_MODE, SEAT, SEATSTATUS, TYPE } from '@/constants';
import { CategoryMixin } from '@/core/mixins/CategoryMixin';
import { ConflictableMixin } from '@/core/mixins/ConflictableMixin';
import { DraggableMixin } from '@/core/mixins/DraggableMixin';
import { LockableMixin } from '@/core/mixins/LockableMixin';
import { PosMixin } from '@/core/mixins/PosMixin';
import { RotatableMixin } from '@/core/mixins/RotatableMixin';
import { SelectableMixin } from '@/core/mixins/SelectableMixin';
import eventbus from 'tiny-emitter/instance';
import { GroupableAndDisabledMixin } from '../mixins/GroupableAndDisabledMixin';
import Model, { ModelParams } from './model';
import { OccupiedMixin } from '@/core/mixins/OccupiedMixin';

import SVG from 'svg.js';
import { SomeRequired } from '../typeHelper';
import { Row } from './row';
import { ReservableMixin } from '../mixins/ReservableMixin';
import { CartableMixin } from '../mixins/CartableMixin';
import { ObstructedMixin } from '../mixins/ObstructedMixin';

export class Seat extends OccupiedMixin(
  ObstructedMixin(
    CartableMixin(
      ReservableMixin(
        GroupableAndDisabledMixin(
          LockableMixin(
            DraggableMixin(RotatableMixin(PosMixin(ConflictableMixin(CategoryMixin(SelectableMixin(Model))))))
          )
        )
      )
    )
  )
) {
  row: Row;
  declare label: SVG.Text;
  declare shape: SVG.Circle;

  constructor(params: SomeRequired<ModelParams, 'row'>) {
    super(params);
    this.row = params.row;
    this.initView();
    if (this.row) {
      this.row.addSeat(this);
    }
  }

  get type() {
    return TYPE.SEAT;
  }

  get block() {
    return this.row?.block;
  }

  get pos() {
    return this.attributes.pos;
  }

  set pos(val) {
    this.attributes.pos = val;
  }

  get t() {
    return this.attributes.t;
  }

  get shapeCenter() {
    return { x: this.shape.cx(), y: this.shape.cy() };
  }

  set t(value: number) {
    this.attributes.t = value;
  }

  get lengthCoeff() {
    return (FULLSEATGAP * this.pos) / this.row.initialLength;
  }

  // @ts-expect-error
  get sid() {
    return this.block.sid;
  }

  get isBooked() {
    return (
      this.attributes.status !== undefined &&
      (this.attributes.status === SEATSTATUS.OCCUPIED || this.attributes.status === SEATSTATUS.RESERVED)
    );
  }

  get seatCanBeSelectedOrDeselected() {
    if (RENDERER_MODE) {
      if (
        this.attributes.status === SEATSTATUS.OCCUPIED ||
        this.attributes.status === SEATSTATUS.RESERVED ||
        this.attributes.status === SEATSTATUS.DISABLED
      ) {
        return false;
      } else if (!this.app.props.isShowCart && this.attributes.status === SEATSTATUS.CART) {
        return false;
      } else if (
        this.locked &&
        (!this.app.props.allowedHolds ||
          this.app.props.channel !== CHANNEL.POS ||
          !this.attributes.holdType ||
          !this.app.props.allowedHolds.includes(this.attributes.holdType))
      ) {
        return false;
      } else if (this.disabled) {
        return false;
      }
    }
    return true;
  }

  get seatCanBeUpdated() {
    if (RENDERER_MODE) {
      if (
        this.attributes.status === SEATSTATUS.OCCUPIED ||
        this.attributes.status === SEATSTATUS.RESERVED ||
        this.attributes.status === SEATSTATUS.DISABLED
      ) {
        return false;
      } else if (!this.app.props.isShowCart && this.attributes.status === SEATSTATUS.CART) {
        return false;
      } else if (this.locked) {
        return false;
      } else if (this.disabled) {
        return false;
      }
    } else if (!RENDERER_MODE && this.app.props.status !== EVENTSTATUS.DRAFT) {
      if (
        this.attributes.status === SEATSTATUS.FREE ||
        this.attributes.status === SEATSTATUS.LOCKED ||
        this.attributes.status === SEATSTATUS.DISABLED
      ) {
        return true;
      } else {
        return false;
      }
    }
    return true;
  }

  updateDynamicElements() {}

  show() {
    // super.show();
    if (RENDERER_MODE || !this.app.showLabels) return;
    this.showLabel();
  }

  hide() {
    // super.hide();
    if (RENDERER_MODE || !this.app.showLabels) return;
    this.hideLabel();
  }

  makeInvisible() {
    // Only allow deletion of free seats
    if (this.isBooked) {
      return;
    }
    this.view.addClass('inv');
    this.attributes.invisible = true;
    // this.attributes.invisible = true;
  }

  makeVisible() {
    this.view.removeClass('inv');
    this.attributes.invisible = false;
    // this.attributes.invisible = false;
  }

  assignCategory(categoryId: number) {
    this.categoryId = categoryId;
  }

  unassignCategory() {
    this.categoryId = null;
  }

  createLabel(x = 0, y = 0) {
    const label = new SVG.Text().plain(this.attributes.label).rebuild(false);
    label.node.setAttribute('x', String(x));
    label.node.setAttribute('y', String(y));
    label.node.setAttribute('text-anchor', 'middle');
    label.node.setAttribute('dominant-baseline', 'central');
    label.node.setAttribute('font-size', String(10 * this.app.seatmap.scale));
    label.node.setAttribute('font-family', 'Roboto');
    label.node.setAttribute('font-style', 'normal');
    label.node.setAttribute('class', 'label seat-label');
    return label;

    // return (
    //   this.view
    //     .plain(this.attributes.label)
    //     .rebuild(false)
    //     // .font({ family: 'Roboto', style: 'normal' })
    //     .attr({
    //       x,
    //       y,
    //       'text-anchor': 'middle',
    //       'dominant-baseline': 'central',
    //       'font-size': 10 * this.app.seatmap.scale,
    //       'font-family': 'Roboto',
    //       'font-style': 'normal',
    //       class: 'label seat-label',
    //     })
    //   // .addClass('label seat-label')
    // );
  }

  changeLabel(label: string = SEAT.LABEL) {
    if (this.app.props.status === EVENTSTATUS.PUBLISHED) {
      return;
    } else if (this.app.props.status === EVENTSTATUS.PAUSED) {
      if (
        this.attributes.status !== SEATSTATUS.FREE &&
        this.attributes.status !== SEATSTATUS.LOCKED &&
        this.attributes.status !== SEATSTATUS.DISABLED
      ) {
        return;
      }
    }
    label = label.trim();
    this.attributes.label = label;
    this.label.plain(this.attributes.label);
    // this.scaleLabel();
  }

  scaleLabel(size = 10) {
    // this.label.font('size', size);
    // let width = this.label.node.textLength.baseVal.value;
    // //firefox fix.
    // if (width <= 0) {
    //   width = this.label.node.getBBox().width;
    //   width -= 4;
    // }
    // const standardSize = size;
    // const optimizedFontSizeFactor = parseFloat(SEAT.INNER_RADIUS / (width + 17));
    // const optimizedFontSize = parseFloat(standardSize * optimizedFontSizeFactor);
    // let fontSize = standardSize;
    // if (optimizedFontSizeFactor <= 1 && optimizedFontSize <= standardSize) {
    //   fontSize = optimizedFontSize;
    // }
    // console.log(size, fontSize * 2/3);
    this.label.font({ size: (size * 2) / 3 });
  }

  clearLabel() {
    this.changeLabel();
  }

  showLabel() {
    this.view.add(this.label);
    // if (!this.label) {
    //   // console.log('showLabel', 'createLabel');
    //   const [cx = 0, cy = 0] = this.attributes.position;
    //   let lcx = cx;
    //   let lcy = cy;
    //   if (this.app.isIE || this.app.isIE9 || this.app.isEdge) {
    //     lcy += 3;
    //     lcx += 0;
    //   }

    //   this.label = this.createLabel(lcx, lcy);
    //   // this.scaleLabel();
    //   // this.label.front();
    // } else if (!this.label.visible()) {
    //   // console.log('showLabel', 'show');
    //   this.label.show();
    // }
  }

  hideLabel() {
    // console.log('hideLabel', 'destroy');
    this.label?.remove();
    // this.label = null;
  }

  onScaleChange(scale: number, cx: number, cy: number) {
    const [x, y] = this.attributes.position;

    const newX = (x - cx) * scale + cx;
    const newY = (y - cy) * scale + cy;

    this.attributes.position = [newX, newY];
    this.moveShapes(newX, newY);
    this.shape.radius((SEAT.INNER_RADIUS / 2) * this.app.seatmap.scale);
    this.label?.font({ size: 10 * this.app.seatmap.scale });
  }

  createSeat() {
    return this.view.circle(SEAT.INNER_RADIUS * this.app.seatmap.scale).addClass('seat-inner');
  }

  // initSeatFromView(view) {
  //   return view.addClass('seat-inner').radius((SEAT.INNER_RADIUS / 2) * this.app.seatmap.scale);
  // }

  initView() {
    const [cx = 0, cy = 0] = this.attributes.position;
    this.view = new SVG.G().addClass('seat-shape');
    this.shape = this.createSeat().center(cx, cy);
    this.label = this.createLabel(cx, cy);
    // this.shape = view ? this.initSeatFromView(view) : this.createSeat();
    // this.view.add(this.shape);

    if (!RENDERER_MODE) {
      //DESIGNER
      // let lcx = cx;
      // let lcy = cy;
      // if (this.app.isIE || this.app.isIE9 || this.app.isEdge) {
      //   lcy += 3;
      //   lcx += 0;
      // }

      // this.label = this.createLabel(lcx, lcy);
      // this.scaleLabel();
      // this.view.add(this.label);
      // this.label.front();

      if (this.categoryId) {
        //CategoryMixin
        this.applyCategoryColor(this.app.getCategoryColor(this.categoryId));
      }

      if (!this.pointOfSale) {
        // POSMixin
        this.pointOfSale = this.app.getPosObj(this.attributes.posId);
      }

      if (this.app.isShowPosColor()) {
        this.applyPosColor(this.app.getPosColor(this.pointOfSale));
      }

      if (this.app.showLabels && this.app.layout != LAYOUT.SECTIONS) {
        this.showLabel();
      }

      if (this.attributes.status === SEATSTATUS.CART) {
        this.shape.addClass('cart');
      } else if (this.attributes.status === SEATSTATUS.RESERVED) {
        this.shape.addClass('reserved');
      } else if (this.attributes.status === SEATSTATUS.DISABLED) {
        this.shape.addClass('disabled');
      } else if (this.attributes.status !== SEATSTATUS.FREE && this.attributes.status !== SEATSTATUS.LOCKED) {
        this.shape.addClass('booked');
      }

      if (this.app.seatmap.store.showLabels) {
        this.showLabel();
      }
    } else {
      //RENDERER
      const lthis = this;

      if (this.attributes.status !== SEATSTATUS.DISABLED) {
        this.shape.mouseover(() => {
          // this.view.fx?.stop();
          // this.view.animate(100).scale(1.2);
          // if (lthis.locked) {
          //   lthis.hideLabel();
          // }
          eventbus.emit('tooltip:show', lthis);
        });

        this.shape.mouseout(() => {
          // this.view.fx?.stop();
          // this.view.animate(100).scale(1);
          // if (lthis.locked) {
          //   lthis.showLabel();
          // }
          eventbus.emit('tooltip:hide', lthis);
          eventbus.emit('hide:alert');
        });
      }

      const map = {
        [SEATSTATUS.FREE]: 'free-seat',
        [SEATSTATUS.LOCKED]: 'locked-seat',
        [SEATSTATUS.OCCUPIED]: 'occupied-seat',
        [SEATSTATUS.RESERVED]: 'reserved-seat',
        [SEATSTATUS.CART]: 'cart-seat',
        [SEATSTATUS.DISABLED]: 'disabled-seat',
        [SEATSTATUS.SELECTED]: 'selected-seat',
      } as const;

      this.view.addClass(map[this.attributes.status]);

      if (this.attributes.status === SEATSTATUS.CART) {
        this.shape.addClass('cart');
      } else if (this.attributes.status === SEATSTATUS.DISABLED) {
        this.shape.addClass('disabled');
      } else if (this.attributes.status === SEATSTATUS.SELECTED) {
        this.shape.addClass('selected');
      } else if (this.attributes.status !== SEATSTATUS.FREE && this.attributes.status !== SEATSTATUS.LOCKED) {
        this.shape.addClass('booked');
      }

      //POS channel
      if (this.app.props.posID !== null && this.app.props.channel === CHANNEL.POS) {
        if (this.attributes.status === SEATSTATUS.RESERVED) {
          this.shape.addClass('reserved');
        }
        if (this.app.posFilterationFail(this)) {
          this.view.addClass('other-pos');
        } else {
          this.view.addClass('current-pos');
        }
      }

      if (this.app.props.channel === CHANNEL.REPORTING) {
        if (this.attributes.status === SEATSTATUS.RESERVED) {
          this.shape.addClass('reserved');
        }
      }

      const color = this.categoryId ? this.app.getCategoryColor(this.categoryId) : '#fff';

      this.view.style({ color });

      if (!this.app.hasActiveCategory() || this.app.isActiveCategory(this.categoryId)) {
        this.view.addClass('active-category');
      }

      //for POS channel
      this.view.addClass(`${this.app.props.channel}-mode`);
    }

    if (this.locked) {
      //LockableMixin
      this.initLockShape();
    }
    if (this.disabled) {
      //DisabledMixin
      this.initDisabledShape();
    }
    if (this.grouped) {
      //GroupableMixin
      this.initGroupedShape();
    }
    if (this.reserved) {
      this.initReservedShape();
    }
    if (this.inCart) {
      this.initCartShape();
    }
    if (this.occupied) {
      //this.initOccupiedShape();
    }
    if (this.obstructed && (!RENDERER_MODE || !(this.locked || this.disabled || this.reserved || this.occupied))) {
      this.initObstructedShape();
    }
    if (this.attributes.invisible) {
      this.view.addClass('inv');
    }
  }

  moveShapes(x: number, y: number) {
    this.label?.attr({ x, y });
    this.shape.center(x, y);
    // if (this.lockShape) this.lockShape.center(x, y);
    // if (this.disabledShape) this.disabledShape.center(x, y);

    if (this.locked) {
      this.updateLockShape(x, y);
    }
    if (this.disabled) {
      this.updateDisabledShape(x, y);
    }
    if (this.reserved) {
      this.updateReservedShape(x, y);
    }
    if (this.inCart) {
      this.updateCartShape(x, y);
    }
    if (this.occupied) {
      //this.updateOccupiedShape();
    }
    if (this.obstructed) {
      this.updateObstructedShape(x, y);
    }
    if (this.isConflicted) {
      this.updateConflictedShape(x, y);
    }
    if (this.selected) {
      this.updateSelectedShape(x, y);
    }
  }

  changeMeta1(value: string) {
    this.setAttribute('meta1', value);
  }

  changeMeta2(value: string) {
    this.setAttribute('meta2', value);
  }

  changeSkew(value: number) {
    this.setAttribute('skew', value);
  }

  changeCurve(value: number) {
    this.setAttribute('curve', value);
  }

  skew(y: number) {
    this.attributes.position[1] += y;
    this.moveShapes(this.attributes.position[0], this.attributes.position[1]);
  }

  curve(x: number, y: number) {
    this.view.untransform();
    this.moveShapes(x, y);
  }

  clone(
    options: {
      keepPosition?: boolean;
      keepLabel?: boolean;
      ts?: number;
      allowedStatus?: (typeof SEATSTATUS)[keyof typeof SEATSTATUS][];
    } = {}
  ) {
    const { keepPosition, keepLabel } = options;
    const json = this.toJSON();
    const [x, y] = json.attributes.position;
    const groupId = json.attributes._groupId;
    if (!keepPosition) json.attributes.position = [x + 50, y + 50];
    if (!keepLabel) delete json.attributes.label;
    if (groupId) {
      const t = options.ts !== undefined ? options.ts : '';
      json.attributes._groupId = json.attributes._groupId + '_' + t;
    }
    json.uuid = this.getUniqueId();

    if (options.allowedStatus && !options.allowedStatus.includes(json.attributes.status)) {
      json.attributes.status = SEATSTATUS.FREE;
    }

    return Seat.fromJSON(json);
  }

  toJSON() {
    const { type, uuid, attributes } = this;
    const [cx, cy] = attributes.position;
    attributes.position = [cx, cy];
    attributes.label = attributes.label === SEAT.LABEL ? '' : attributes.label;
    return Seat.filteredJSON({
      uuid,
      type,
      attributes,
    }) as SomeRequired<ModelParams, 'row'>;
  }

  destroyView() {
    this.view.remove();
  }

  destroy(forced = false) {
    if (!forced) {
      if (
        (this.app.props.status === EVENTSTATUS.PUBLISHED || this.app.props.status === EVENTSTATUS.PAUSED) &&
        this.attributes.status !== SEATSTATUS.FREE &&
        this.attributes.status !== SEATSTATUS.DISABLED
      ) {
        return;
      }
    }
    this.destroyView();
    this.row.removeSeat(this);
    eventbus.emit('seat:removed', this);
  }

  static fromJSON(params: SomeRequired<ModelParams, 'row'>) {
    params.attributes.label =
      params.attributes.label !== undefined && params.attributes.label !== '' ? params.attributes.label : SEAT.LABEL;
    return Seat.create(params);
  }

  static create(params: SomeRequired<ModelParams, 'row'>) {
    const attributes = {
      label: SEAT.LABEL,
      position: [0, 0],
      pos: 0,
      t: 0,
      skew: 0,
      curve: 0,
      status: SEATSTATUS.FREE,
    };

    params.attributes = Object.assign({}, attributes, params.attributes);
    return new Seat(params);
  }
}
